import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!sectionId) {
      throw new Error('A section ID is required');
    }

    // For non-recent intents we must have a product id.
    if (intent !== 'recent' && !productId) {
      throw new Error('Product ID is required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    /* CUSTOM CODE: Support intent=recent using localStorage history */
    const fetchPromise =
      intent === 'recent'
        ? this.#fetchCachedRecentlyViewed(sectionId, productId || '')
        : this.#fetchCachedRecommendations(productId || '', sectionId, intent);

    fetchPromise
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          const html = intent === 'recent' ? this.#withCurrentAttributes(result.data) : result.data;
          morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
    /* END CUSTOM CODE */
  }

  /* CUSTOM CODE: "recent" is rendered on the search URL, where recommendations.performed is false and
     there is no product id. Morphing those attributes back onto this element would trigger the
     MutationObserver -> reload -> morph in an endless loop (page freeze), so keep ours. */
  /**
   * @param {string} html
   * @returns {string}
   */
  #withCurrentAttributes(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('product-recommendations');
    if (!incoming) return html;

    for (const { name } of [...incoming.attributes]) incoming.removeAttribute(name);
    for (const { name, value } of [...this.attributes]) incoming.setAttribute(name, value);
    incoming.dataset.recommendationsPerformed = 'true';

    return doc.body.innerHTML;
  }

  /* CUSTOM CODE: Fetch recently viewed products markup */
  /**
   * Fetch recently viewed products (in order) by rendering this section on the search URL.
   * @param {string | undefined} sectionId
   * @param {string} currentProductId
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecentlyViewed(sectionId, currentProductId) {
    if (!sectionId) {
      return { success: false, status: 400 };
    }

    const viewedProducts = RecentlyViewed.getProducts().filter(
      (/** @type {string} */ id) => id && id !== currentProductId
    );

    if (viewedProducts.length === 0) {
      return { success: false, status: 204 };
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', sectionId);

    return this.#fetchCachedUrl(url.toString());
  }

  /**
   * Fetch a URL and cache its response.
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedUrl(url) {
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }
  /* END CUSTOM CODE */

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    return this.#fetchCachedUrl(url);
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
